import { isTeamsAppEnabled as isTeamsAppEnabledBase } from '@atlaskit/teams-app-config/is-teams-app-enabled';
import {
	navigateToTeamsApp as navigateToTeamsAppBase,
	toTeamsAppURL as toTeamsAppURLBase,
} from '@atlaskit/teams-app-config/navigation';
import type {
	NavigationActionPayloadVariants,
	NavigationResult,
} from '@atlaskit/teams-app-config/types';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import getMeta from '@atlassian/jira-get-meta';

export const useNavigateToTeamsApp = (
	navActionPayload: NavigationActionPayloadVariants,
	shouldOpenInSameTab?: boolean,
): NavigationResult => {
	const { data: orgId } = useOrgId();
	const cloudId = useCloudId();
	const { push } = useRouterActions();

	if (!orgId && !cloudId) {
		throw new Error('Org ID or Cloud ID is required to navigate to Teams App');
	}

	return navigateToTeamsAppBase({
		push,
		hostProduct: 'jira',
		orgId,
		cloudId,
		userHasNav4Enabled: getWillShowNav4(),
		shouldOpenInSameTab,
		...navActionPayload,
	});
};

export const getNavigateToTeamsApp = (
	navActionPayload: NavigationActionPayloadVariants,
	shouldOpenInSameTab?: boolean,
): NavigationResult => {
	const orgId = getMeta('ajs-org-id');
	const cloudId = getMeta('ajs-cloud-id');

	return navigateToTeamsAppBase({
		hostProduct: 'jira',
		orgId: orgId || '',
		cloudId: cloudId || undefined,
		userHasNav4Enabled: getWillShowNav4(),
		shouldOpenInSameTab,
		...navActionPayload,
	});
};

export const useTransformToTeamsAppUrl = (path: string, search = ''): string => {
	const { data: orgId } = useOrgId();
	const cloudId = useCloudId();

	return toTeamsAppURLBase(path, search, {
		orgId,
		cloudId,
	});
};

export const toTeamsAppURL = (path: string, search = ''): string => {
	const orgId = getMeta('ajs-org-id');
	const cloudId = getMeta('ajs-cloud-id');

	return toTeamsAppURLBase(path, search, {
		orgId: orgId ?? '',
		cloudId: cloudId ?? '',
	});
};

export const isTeamsAppEnabled = (): boolean => {
	return isTeamsAppEnabledBase({
		hostProduct: 'jira',
		userHasNav4Enabled: getWillShowNav4(),
	});
};
